/**
 * Application API service.
 * Mirrors Flutter IApplicationRepository — all application, resume, and form endpoints.
 *
 * Flutter source:
 *   lib/infrastructure/application/i_application_repository.dart
 *   lib/infrastructure/web_home/i_web_home_repository.dart
 *
 * Every call here is made with plain `http` or a bare `Dio()` in Flutter (no HttpInterCeptor),
 * so none of them get the interceptor's 409 → /duplicateAccount handling.
 */
import apiClient from './apiClient';
import type {
  MasterDataOption,
  OptionData,
  UploadResumeResponse,
} from '@/types/application.types';

const NO_INTERCEPTOR = { skipErrorInterceptor: true } as const;

/**
 * Flutter ErrorConstants.emptyFile. Both resume uploads return it when the server answers 204
 * (i_application_repository.dart uploadResumeForm / uploadResume). The upload functions throw
 * `new Error(EMPTY_FILE_ERROR)` so callers can show this text instead of their generic failure.
 */
export const EMPTY_FILE_ERROR = 'Please upload new resume this file appears to be empty';

/**
 * Flutter's resume uploads succeed only on 200: 204 is ErrorConstants.emptyFile and any other
 * status is a failure (ErrorConstants.genericError).
 */
function assertResumeUploaded(status: number): void {
  if (status === 204) throw new Error(EMPTY_FILE_ERROR);
  if (status !== 200) throw new Error(`Resume upload failed with status ${status}`);
}

/* ────────────────────────────── Form Content / Master Data ────────────────────────────── */

/**
 * GET /v1/application/_masterV2
 * Mirrors Flutter IApplicationRepository.getMasterData().
 * Returns default candidate data (names, email, etc.) when no draft exists.
 */
export async function getMasterData(): Promise<unknown> {
  const res = await apiClient.get('v1/application/_masterV2', NO_INTERCEPTOR);
  return res.data;
}

/**
 * GET /v1/application/requisition-displayId/{reqDisplayId}
 * Mirrors Flutter IApplicationRepository.getPartialApplnFormData().
 * Returns an existing draft application for a given requisition displayId.
 */
export async function getPartialApplicationFormData(
  reqDisplayId: string,
): Promise<unknown> {
  const res = await apiClient.get(
    `v1/application/requisition-displayId/${reqDisplayId}`,
    NO_INTERCEPTOR,
  );
  return res.data;
}

/**
 * GET /v1/application/{applicationId}
 * Mirrors Flutter IApplicationRepository.getDetailsByApplicationId().
 * Returns full application details for a known applicationId.
 */
export async function getDetailsByApplicationId(
  applicationId: string,
): Promise<unknown> {
  const res = await apiClient.get(`v1/application/${applicationId}`, NO_INTERCEPTOR);
  return res.data;
}

/**
 * GET /v1/p/generic-data/lookupV2?type={masterDataKey}&page={page}&key={typedKey}
 * Mirrors Flutter IApplicationRepository.getMasterDataForApplnForm().
 * Returns lookup options for searchable dropdowns (e.g. skills, qualifications).
 */
export async function getMasterDataForFormField(
  masterDataKey: string,
  page: number = 1,
  typedKey?: string,
): Promise<MasterDataOption[]> {
  let url = `v1/generic-data/lookupV2?type=${masterDataKey}&page=${page}`;
  if (typedKey && typedKey.length > 0) {
    url += `&key=${typedKey}`;
  }
  const res = await apiClient.get<MasterDataOption[]>(url, NO_INTERCEPTOR);
  return res.data ?? [];
}

/* ────────────────────────────── Resume Upload ────────────────────────────── */

/**
 * POST /v1/application/{candidateId}/{requisitionId}/upload/resume
 * Mirrors Flutter IApplicationRepository.uploadResumeForm().
 * Multipart form upload within application context.
 * Throws `new Error(EMPTY_FILE_ERROR)` on 204, and an error on any other non-200 status.
 */
export async function uploadResumeForApplication(
  file: File,
  candidateId: string,
  requisitionId: string,
  extraHeaders?: Record<string, string>,
  extraBody?: Record<string, unknown>,
  onProgress?: (percent: number) => void,
): Promise<UploadResumeResponse> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  if (extraBody) {
    Object.entries(extraBody).forEach(([k, v]) => {
      formData.append(k, String(v));
    });
  }
  const res = await apiClient.post<UploadResumeResponse>(
    `v1/application/${candidateId}/${requisitionId}/upload/resume`,
    formData,
    {
      ...NO_INTERCEPTOR,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...extraHeaders,
      },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    },
  );
  assertResumeUploaded(res.status);
  return res.data;
}

/**
 * POST /v1/candidate/upload/resume
 * Mirrors Flutter IApplicationRepository.uploadResume().
 * Standalone resume upload (not within application context — used from Home/Profile).
 * Throws `new Error(EMPTY_FILE_ERROR)` on 204, and an error on any other non-200 status.
 */
export async function uploadResumeStandalone(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const res = await apiClient.post('v1/candidate/upload/resume', formData, {
    ...NO_INTERCEPTOR,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (evt) => {
      if (onProgress && evt.total) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  });
  assertResumeUploaded(res.status);
  return res.data;
}

/**
 * GET /v1/candidate/download/resume
 * Mirrors Flutter IApplicationRepository.downloadResume().
 * Returns raw bytes + content-disposition header.
 */
export async function downloadResume(): Promise<{
  data: Blob;
  fileName: string;
}> {
  const res = await apiClient.get('v1/candidate/download/resume', {
    ...NO_INTERCEPTOR,
    responseType: 'blob',
  });
  let fileName = 'resume';
  const disposition = res.headers['content-disposition'] as string | undefined;
  if (disposition && disposition.includes('resumes/uploads/')) {
    fileName = disposition.split('resumes/uploads/')[1]?.replace(/"$/, '') ?? fileName;
  }
  return { data: res.data as Blob, fileName };
}

/* ────────────────────────────── Resume Extraction ────────────────────────────── */

/**
 * POST /v1/p/skill/_extract?skillDataExtraction=true&metaDataExtraction=false
 * Mirrors Flutter IWebHomeRepository.extractResume().
 * Extracts skills from uploaded resume file.
 */
export async function extractResumeSkills(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const res = await apiClient.post(
    'v1/p/skill/_extract?skillDataExtraction=true&metaDataExtraction=false',
    formData,
    {
      ...NO_INTERCEPTOR,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      },
    },
  );
  // Flutter: 204 → ErrorConstants.noContent (nothing could be extracted)
  if (res.status === 204) return null;
  return res.data;
}

/**
 * POST /v1/p/fetch/master-data-from-resume
 * Mirrors Flutter IApplicationRepository.fetchMasterDataFromResume().
 * Extracts candidate personal data from uploaded resume.
 */
export async function fetchMasterDataFromResume(
  file: File,
): Promise<unknown> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const res = await apiClient.post(
    'v1/p/fetch/master-data-from-resume',
    formData,
    {
      ...NO_INTERCEPTOR,
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return res.data;
}

/* ────────────────────────────── Application CRUD ────────────────────────────── */

/**
 * POST /v1/application/{candidateId}/{requisitionId}
 * Mirrors Flutter IApplicationRepository.saveApplication() — CREATE case.
 * Returns the applicationId as `key`.
 */
export async function createApplication(
  candidateId: string,
  requisitionId: string,
  payload: Record<string, unknown>,
): Promise<string> {
  const res = await apiClient.post<{ key: string }>(
    `v1/application/${candidateId}/${requisitionId}`,
    payload,
    NO_INTERCEPTOR,
  );
  return res.data.key;
}

/**
 * PUT /v1/application/{candidateId}/{requisitionId}/{applicationId}
 * Mirrors Flutter IApplicationRepository.saveApplication() — UPDATE case.
 * Returns the applicationId as `key`.
 */
export async function updateApplication(
  candidateId: string,
  requisitionId: string,
  applicationId: string,
  payload: Record<string, unknown>,
): Promise<string> {
  const res = await apiClient.put<{ key: string }>(
    `v1/application/${candidateId}/${requisitionId}/${applicationId}`,
    payload,
    NO_INTERCEPTOR,
  );
  return res.data.key;
}

/**
 * POST or PUT /v1/application/{candidateId}/{requisitionId}[/{applicationId}]
 * Combined save — mirrors Flutter IApplicationRepository.saveApplication() logic.
 * If applicationId is present → PUT (update), else → POST (create).
 */
export async function saveApplication(
  candidateId: string,
  requisitionId: string,
  applicationId: string | undefined,
  payload: Record<string, unknown>,
): Promise<string> {
  if (applicationId && applicationId.length > 0) {
    return updateApplication(candidateId, requisitionId, applicationId, payload);
  }
  return createApplication(candidateId, requisitionId, payload);
}

/* ────────────────────────────── Location Suggestions ────────────────────────────── */

type PlaceType = 'country' | 'state' | 'city';

/**
 * GET /v1/p/location/_suggest — options for the location dropdowns of the application form.
 * Mirrors Flutter IJobSearchBarRepository:
 *   - locationSuggestionsForm  (placeType state/city, optional country/state filter)
 *   - countrySuggestionsForm   (placeType country, paged)
 *   - locationSuggestions      (placeType city, paged, labelled by fqLocationName)
 * Each item maps `key<Label>` → key and `<label>` → displayKey.
 */
export async function getPlaceSuggestions(params: {
  placeType: PlaceType;
  country?: string;
  state?: string;
  key?: string;
  page?: number;
  /** Response field used as the label; defaults to the place type (Flutter otherFieldFetch). */
  labelField?: string;
}): Promise<OptionData[]> {
  const query = new URLSearchParams({ placeType: params.placeType });
  if (params.country) query.set('country', params.country);
  if (params.state) query.set('state', params.state);
  if (params.key) query.set('key', params.key);
  if (params.page) query.set('page', String(params.page));
  const res = await apiClient.get<Array<Record<string, unknown>>>(
    `v1/p/location/_suggest?${query}`,
    NO_INTERCEPTOR,
  );

  const labelField = params.labelField ?? params.placeType;
  const keyField = `key${labelField.charAt(0).toUpperCase()}${labelField.slice(1)}`;
  return (res.data ?? [])
    .map((item) => ({
      key: String(item[keyField] ?? item[labelField] ?? ''),
      displayKey: String(item[labelField] ?? ''),
    }))
    .filter((opt) => opt.displayKey);
}
